import React from 'react';
import {
  ArrowDown,
  ArrowRight,
  Bot,
  Calendar,
  ClipboardCheck,
  Code2,
  FileText,
  ListChecks,
  MessagesSquare,
  Phone,
  Rocket,
  UserCheck,
  UserRound,
  Users,
  Video,
} from 'lucide-react';
import { Button } from '@/Components/ui/button';
import Header from '../Header&Footer/Header';
import Footer from '../Header&Footer/Footer';
import useSEO from '../HomePage/useSEO';
import ScrollReveal from '../HomePage/ScrollReveal';
import CaseStudyProof, { caseStudyProofs } from '../../../Blog/CaseStudyProof';
import {
  CapabilityGrid,
  CONTACT_PATH,
  DEMO_PATH,
  IndustryCta,
  IndustryFlowStyles,
  IndustryWorkflow,
  Section,
  SectionIntro,
} from '../Industries/industryUi';

const HANDLES = [
  { title: 'Create JDs', detail: 'Turn the role into a brief the team can hire against.', icon: FileText },
  { title: 'Screen applicants', detail: 'Read resumes against that role before a founder spends time.', icon: ClipboardCheck },
  { title: 'Conduct interviews', detail: 'Run structured interviews the team can review.', icon: Video },
  { title: 'Run technical assessments', detail: 'Use a coding or role assessment when the job needs it.', icon: Code2 },
  { title: 'Verify candidate information', detail: 'Check the details the role depends on.', icon: UserCheck },
  { title: 'Rank candidates', detail: 'Order applicants by fit so the shortlist is easier to read.', icon: ListChecks },
  { title: 'Schedule interviews', detail: 'Move finalists onto a calendar the founder can use.', icon: Calendar },
  { title: 'Keep candidates engaged', detail: 'Stay in touch while the team decides.', icon: MessagesSquare },
];

const WORKFLOW = [
  { title: 'Founder creates role', detail: 'The founder writes the role. SearchJob uses that brief for the rest of the path.', icon: FileText },
  { title: 'AI screens applicants', detail: 'Resumes are read against the role before a live conversation.', icon: ClipboardCheck },
  { title: 'AI interviews candidates', detail: 'A structured interview is saved for the team to review.', icon: Video },
  { title: 'Assessment', detail: 'A coding or role assessment is added when the job needs it.', icon: Code2 },
  { title: 'Recruiter review', detail: 'A person checks the records before anyone is sent to the founder.', icon: UserRound },
  { title: 'Founder meets finalists', detail: 'The founder spends time on the shortlist, not the full applicant pile.', icon: Users },
  { title: 'Hire', detail: 'The founder makes the hiring decision.', icon: UserCheck },
];

const BEST_FOR = [
  'Seed to Series C startups',
  'First 10–50 hires',
  'Engineering teams',
  'Sales and operations teams',
  'Fast hiring after fundraising',
];

const CAPABILITIES = [
  {
    title: 'AI Sourcing & Screening',
    helps: 'Who enters the pipeline, and how the resume fits the role.',
    href: '/hirewithus/ai-sourcing-screening',
    icon: FileText,
  },
  {
    title: 'AI Call Screening',
    helps: 'How a candidate answers role questions on a structured call.',
    href: '/hirewithus/ai-call-screening',
    icon: Phone,
  },
  {
    title: 'Coding Assessment',
    helps: 'Whether technical candidates can do the work.',
    href: '/hirewithus/coding-assessment',
    icon: Code2,
  },
  {
    title: 'Screening Assessment',
    helps: 'How the resume scores against the job description.',
    href: '/hirewithus/screening-assessment',
    icon: ClipboardCheck,
  },
  {
    title: 'Functional Assessment',
    helps: 'Behaviour, communication, and role-based skills.',
    href: '/hirewithus/functional-assessment',
    icon: Users,
  },
  {
    title: 'Virtual Hiring',
    helps: 'A structured AI interview the team can review.',
    href: '/hirewithus/virtualhiring',
    icon: Video,
  },
];

const AI_WORK = ['Screening', 'Assessment', 'Repetitive interviews', 'Candidate evidence'];
const HUMAN_WORK = ['Recruiter review', 'Founder review', 'Final decision'];

const SHORTLIST = [
  { label: 'Founder creates the role', note: 'The brief the rest of the path uses' },
  { label: 'Applicants are screened', note: 'Resume and early answers' },
  { label: 'A shortlist is prepared', note: 'Reviewed before it is sent on' },
  { label: 'Founder meets finalists', note: 'The hiring decision stays with the founder' },
];

export default function StartupHiring() {
  useSEO({
    title: 'AI Recruitment for Startups | SearchJob.ai',
    description:
      'Hire startup teams faster with AI resume screening, interviews, assessments, voice screening and recruiter support.',
    canonical: 'https://searchjob.ai/solutions/startup-hiring',
    ogImage: 'https://searchjob.ai/og-image.png',
  });

  return (
    <div className="min-h-screen overflow-x-hidden bg-background">
      <Header />
      <main className="pt-16">
        <Hero />
        <ScrollReveal>
          <Handles />
        </ScrollReveal>
        <ScrollReveal>
          <Workflow />
        </ScrollReveal>
        <ScrollReveal>
          <BestFor />
        </ScrollReveal>
        <ScrollReveal>
          <Capabilities />
        </ScrollReveal>
        <ScrollReveal>
          <HumanLoop />
        </ScrollReveal>
        <ScrollReveal>
          <Proof />
        </ScrollReveal>
        <FinalCta />
      </main>
      <Footer />
      <IndustryFlowStyles />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative isolate overflow-hidden bg-background">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute right-0 top-0 h-64 w-64 rounded-full bg-primary/5 blur-3xl" />
      </div>
      <div className="mx-auto grid max-w-7xl items-start gap-12 px-4 pt-12 pb-16 sm:px-6 lg:grid-cols-2 lg:gap-14 lg:px-8 lg:pt-16">
        <div className="text-center lg:text-left">
          <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
            <Rocket className="h-3.5 w-3.5" aria-hidden="true" />
            Startup hiring
          </p>
          <h1 className="mb-6 text-4xl font-bold leading-[1.06] tracking-[-0.02em] text-foreground sm:text-5xl lg:text-[3.4rem] xl:text-6xl">
            Build Your Team <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">Before Your Competitors Do.</span>
          </h1>
          <p className="mx-auto mb-8 max-w-2xl text-lg leading-relaxed text-muted-foreground sm:text-xl lg:mx-0">
            Your startup doesn't need 10 recruiters. It needs a hiring system that works like 10 recruiters.
          </p>
          <div className="flex flex-col justify-center gap-3 sm:flex-row lg:justify-start">
            <Button
              asChild
              size="lg"
              className="!h-12 !rounded-full !bg-primary px-8 text-base font-semibold !text-primary-foreground shadow-lg shadow-primary/25 group hover:!bg-primary/90"
            >
              <a href={DEMO_PATH}>
                Hire Your Next 10 People With AI
                <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="!h-12 !rounded-full bg-card/70 px-8 text-base font-semibold backdrop-blur-md"
            >
              <a href={CONTACT_PATH}>Talk to a Hiring Expert</a>
            </Button>
          </div>
        </div>
        <ShortlistBoard />
      </div>
    </section>
  );
}

function ShortlistBoard() {
  return (
    <div className="relative self-start" aria-hidden="true">
      <div className="pointer-events-none absolute -inset-4 -z-10 rounded-[3rem] bg-gradient-to-tr from-primary/20 via-emerald-500/10 to-teal-500/20 blur-3xl" />
      <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Example shortlist path</p>
      <ol className="space-y-3">
        {SHORTLIST.map((step, index) => (
          <li key={step.label}>
            <div className="flex items-center gap-3 rounded-2xl border border-primary bg-primary/5 px-4 py-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-primary/30 bg-primary/10 text-sm font-semibold text-primary">
                {index + 1}
              </span>
              <span>
                <span className="block text-sm font-semibold text-foreground">{step.label}</span>
                <span className="block text-sm text-muted-foreground">{step.note}</span>
              </span>
            </div>
            {index < SHORTLIST.length - 1 ? <ArrowDown className="mx-auto my-1 h-4 w-4 text-primary/70" /> : null}
          </li>
        ))}
      </ol>
    </div>
  );
}

function Handles() {
  return (
    <Section muted>
      <SectionIntro
        eyebrow="What SearchJob handles"
        title="The hiring work a lean team should not do by hand."
        copy="SearchJob covers the repetitive steps. The founder still decides who joins."
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {HANDLES.map((item) => (
          <article
            key={item.title}
            className="group rounded-2xl border border-primary bg-primary/5 p-5 transition duration-200 hover:-translate-y-0.5 hover:border-primary hover:shadow-md motion-reduce:transition-none motion-reduce:hover:translate-y-0 motion-reduce:hover:shadow-none"
          >
            <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 text-primary transition-colors duration-200 group-hover:border-primary">
              <item.icon className="h-5 w-5" aria-hidden="true" />
            </span>
            <h3 className="text-base font-semibold text-foreground">{item.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.detail}</p>
          </article>
        ))}
      </div>
    </Section>
  );
}

function Workflow() {
  return (
    <Section id="startup-workflow">
      <SectionIntro
        eyebrow="Startup hiring workflow"
        title="From the role to a founder decision."
        copy="Select a step. The path is the same idea for each role. The checks still depend on the job."
      />
      <IndustryWorkflow steps={WORKFLOW} uniformCards plainDetail />
    </Section>
  );
}

function BestFor() {
  return (
    <Section muted>
      <SectionIntro
        eyebrow="Best for"
        title="Teams hiring their first people."
        copy="These are the situations this page is written for."
      />
      <ul className="flex flex-wrap gap-3">
        {BEST_FOR.map((item) => (
          <li key={item}>
            <span className="inline-flex min-h-11 items-center rounded-full border border-primary/30 bg-primary/5 px-4 text-sm font-medium text-foreground">
              {item}
            </span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function Capabilities() {
  return (
    <Section>
      <SectionIntro
        eyebrow="Relevant capabilities"
        title="Products already on SearchJob.ai."
        copy="These are the tools behind startup hiring. This page is about the hiring path, not a full catalog."
      />
      <CapabilityGrid items={CAPABILITIES} />
    </Section>
  );
}

function HumanLoop() {
  return (
    <Section muted>
      <SectionIntro
        eyebrow="AI and people"
        titleClassName="text-2xl sm:text-3xl"
        title="AI handles repetitive hiring work. Humans make the final hiring decisions."
        copy="SearchJob.ai collects evidence. It does not make the hiring decision."
      />
      <article className="overflow-hidden rounded-3xl border border-primary bg-primary/5 shadow-sm">
        <div className="grid md:grid-cols-2">
          <ResponsibilityGroup
            eyebrow="AI prepares"
            title="Consistent evidence, ready for review"
            description="Repeatable work is completed before your team spends time on a candidate."
            icon={Bot}
            items={AI_WORK}
            toned
          />
          <ResponsibilityGroup
            eyebrow="Your team decides"
            title="Context and judgment stay with people"
            description="Recruiters and founders review the evidence and choose who moves forward."
            icon={UserCheck}
            items={HUMAN_WORK}
            separated
          />
        </div>
        <footer className="flex items-center gap-3 border-t border-primary/15 bg-muted/30 px-5 py-4 sm:px-6">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <FileText className="h-4 w-4" aria-hidden="true" />
          </span>
          <p className="text-sm font-medium text-foreground">Evidence moves to your team. The final hiring decision stays with people.</p>
        </footer>
      </article>
    </Section>
  );
}

function ResponsibilityGroup({ eyebrow, title, description, icon: Icon, items, toned = false, separated = false }) {
  return (
    <section className={`p-5 sm:p-6 ${toned ? 'bg-primary/[0.045]' : ''} ${separated ? 'border-t border-primary/15 md:border-l md:border-t-0' : ''}`}>
      <header className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-primary/20 bg-primary/10 text-primary">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
        <span>
          <span className="block text-xs font-semibold uppercase tracking-wider text-primary">{eyebrow}</span>
          <h3 className="mt-1 text-lg font-semibold tracking-tight text-foreground">{title}</h3>
        </span>
      </header>
      <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{description}</p>
      <ul className="mt-5 grid gap-x-5 gap-y-3 sm:grid-cols-2">
        {items.map((item) => (
          <li key={item} className="flex items-center gap-2 text-sm font-medium text-foreground">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Proof() {
  return (
    <Section>
      <SectionIntro
        eyebrow="Customer proof"
        title="Published stories for early-stage and growing teams."
        copy="manest.ai covers screening before live interviews for an early-stage technical team. Kinzy Club covers skills, attitude, and culture for a growing brand."
      />
      <CaseStudyProof items={[caseStudyProofs.manest, caseStudyProofs.kinzy]} />
    </Section>
  );
}

function FinalCta() {
  return (
    <IndustryCta
      title="Hire Your Next 10 People With AI"
      copy="Use SearchJob.ai to screen, assess and interview candidates before your team spends time on final decisions."
      primaryLabel="Book a Hiring Consultation"
      primaryHref={DEMO_PATH}
      secondaryLabel="Contact Us"
      secondaryHref={CONTACT_PATH}
    />
  );
}
